<template>
  <div class="share-notice">
    <button
      ref="bell"
      type="button"
      class="share-notice__bell"
      :class="{ 'is-open': panelOpen, 'has-unread': unreadCount > 0 }"
      aria-label="分享提醒"
      :aria-expanded="panelOpen ? 'true' : 'false'"
      @click.stop="togglePanel"
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
        <path d="M18 8a6 6 0 1 0-12 0c0 6-2 7-2 7h16s-2-1-2-7" />
        <path d="M10.3 20a2 2 0 0 0 3.4 0" />
      </svg>
      <span v-if="unreadCount > 0" class="share-notice__badge">
        {{ unreadCount > 99 ? '99+' : unreadCount }}
      </span>
    </button>

    <transition name="sn-fade">
      <div
        v-if="panelOpen"
        class="share-notice__mask"
        :class="{ 'is-sheet': isNarrow }"
        @click="closePanel"
      />
    </transition>

    <transition name="sn-panel">
      <section
        v-if="panelOpen"
        ref="panel"
        class="share-notice__panel"
        :class="{ 'is-sheet': isNarrow }"
        :style="panelStyle"
        role="dialog"
        aria-label="分享提醒"
        tabindex="-1"
        @click.stop
      >
        <header class="sn-head">
          <h4>分享提醒</h4>
          <button
            v-if="notices.length"
            type="button"
            class="sn-link"
            @click="ackAll"
          >全部已读</button>
        </header>

        <div v-if="notices.length" class="sn-list">
          <article
            v-for="item in notices"
            :key="item.paperId"
            class="sn-item"
          >
            <span class="sn-avatar" :style="avatarStyle(item)">{{ initialOf(item) }}</span>
            <div class="sn-item__main">
              <div class="sn-item__head">
                <b>{{ item.sharerName }}</b>
                <span>分享了问卷</span>
                <time>{{ formatTime(item.shareTime) }}</time>
              </div>
              <div class="sn-item__paper" :title="item.paperName">{{ item.paperName }}</div>
              <div class="sn-item__ops">
                <button type="button" class="sn-link" @click="openPaper(item)">查看问卷</button>
                <button type="button" class="sn-link is-muted" @click="ackOne(item)">知道了</button>
              </div>
            </div>
          </article>
        </div>

        <div v-else class="sn-empty">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M18 8a6 6 0 1 0-12 0c0 6-2 7-2 7h16s-2-1-2-7" />
            <path d="M10.3 20a2 2 0 0 0 3.4 0" />
          </svg>
          <p>暂无新的分享提醒</p>
        </div>

        <footer class="sn-foot">仅保留最近 30 天的提醒，处理后会立即消失</footer>
      </section>
    </transition>

    <div
      v-if="cardsVisible && cardItems.length"
      class="share-notice__cards"
      :class="{ 'is-narrow': isNarrow }"
      @mouseenter="pauseCards"
      @mouseleave="resumeCards"
    >
      <transition-group name="sn-card" tag="div" class="sn-card-list">
        <article v-for="item in cardItems" :key="'card-' + item.paperId" class="sn-card">
          <span class="sn-avatar" :style="avatarStyle(item)">{{ initialOf(item) }}</span>
          <div class="sn-card__main">
            <div class="sn-card__head">
              <b>{{ item.sharerName }}</b>
              <span>分享了问卷</span>
              <time>{{ formatTime(item.shareTime) }}</time>
            </div>
            <div class="sn-card__paper" :title="item.paperName">{{ item.paperName }}</div>
            <div class="sn-card__ops">
              <button type="button" class="sn-link" @click="openPaper(item)">查看问卷</button>
              <button type="button" class="sn-link is-muted" @click="ackOne(item)">知道了</button>
            </div>
          </div>
          <button type="button" class="sn-card__close" aria-label="收起" @click="closeCards">×</button>
        </article>
        <div v-if="extraCount > 0" key="more" class="sn-card__more">
          还有 {{ extraCount }} 条提醒，点右上角铃铛查看
        </div>
      </transition-group>
    </div>
  </div>
</template>

<script>
import {
  listShareNotices,
  ackShareNotices,
  ackAllShareNotices
} from '@/api/custom/shareNotice'

const CARD_DURATION = 8000
const CARD_VISIBLE_LIMIT = 2
const POLL_INTERVAL = 60000

let loginCardsShown = false

const AVATAR_COLORS = [
  { bg: '#e8f1fe', color: '#1890ff' },
  { bg: '#f3ecff', color: '#7b61ff' },
  { bg: '#e7f7f1', color: '#16928a' },
  { bg: '#fff2e8', color: '#d46b08' }
]

function normalizeNotice(raw) {
  if (!raw || raw.paperId == null || String(raw.paperId).trim() === '') {
    return null
  }
  return {
    paperId: String(raw.paperId).trim(),
    paperName: raw.paperName != null && String(raw.paperName).trim() !== ''
      ? String(raw.paperName)
      : '未命名问卷',
    sharerName: raw.sharerName != null && String(raw.sharerName).trim() !== ''
      ? String(raw.sharerName)
      : (raw.sharerNo != null ? String(raw.sharerNo) : '同事'),
    sharerNo: raw.sharerNo != null ? String(raw.sharerNo) : '',
    shareTime: Number(raw.shareTime) || 0
  }
}

function pad(value) {
  return value < 10 ? `0${value}` : String(value)
}

export default {
  name: 'ShareNotice',
  data() {
    return {
      notices: [],
      panelOpen: false,
      panelPos: { top: 0, left: 0 },
      viewportWidth: window.innerWidth,
      cardsVisible: false,
      cardHovering: false,
      loading: false,
      stopped: false,
      pollTimer: null,
      cardTimer: null
    }
  },
  computed: {
    unreadCount() {
      return this.notices.length
    },
    isNarrow() {
      return this.viewportWidth <= 640
    },
    panelStyle() {
      if (this.isNarrow) {
        return {}
      }
      return {
        top: `${this.panelPos.top}px`,
        left: `${this.panelPos.left}px`
      }
    },
    cardItems() {
      return this.notices.slice(0, CARD_VISIBLE_LIMIT)
    },
    extraCount() {
      return Math.max(0, this.notices.length - CARD_VISIBLE_LIMIT)
    }
  },
  mounted() {
    this.fetchNotices(true)
    this.pollTimer = setInterval(() => {
      if (document.hidden) {
        return
      }
      this.fetchNotices(false)
    }, POLL_INTERVAL)
    window.addEventListener('focus', this.handleWindowFocus)
    window.addEventListener('keydown', this.handleKeydown)
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    clearInterval(this.pollTimer)
    clearTimeout(this.cardTimer)
    window.removeEventListener('focus', this.handleWindowFocus)
    window.removeEventListener('keydown', this.handleKeydown)
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    fetchNotices(initial) {
      if (this.loading || this.stopped) {
        return
      }
      this.loading = true
      listShareNotices({ limit: 20 })
        .then((response) => {
          if (!response || response.code !== 0) {
            return
          }
          const rows = Array.isArray(response.data) ? response.data : []
          this.notices = rows.map(normalizeNotice).filter(Boolean)
          if (initial && !loginCardsShown && this.notices.length) {
            this.showLoginCards()
          }
        })
        .catch((error) => {
          if (this.isSessionError(error)) {
            this.stopped = true
            this.closePanel()
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    isSessionError(error) {
      if (!error) {
        return false
      }
      if (error.code === 2 || error.code === 401) {
        return true
      }
      return !!(error.response && error.response.status === 401)
    },
    showLoginCards() {
      loginCardsShown = true
      this.cardsVisible = true
      this.scheduleCardClose()
    },
    scheduleCardClose() {
      clearTimeout(this.cardTimer)
      this.cardTimer = setTimeout(() => {
        if (this.cardHovering) {
          return
        }
        this.cardsVisible = false
      }, CARD_DURATION)
    },
    pauseCards() {
      this.cardHovering = true
      clearTimeout(this.cardTimer)
    },
    resumeCards() {
      this.cardHovering = false
      if (this.cardsVisible) {
        this.scheduleCardClose()
      }
    },
    closeCards() {
      clearTimeout(this.cardTimer)
      this.cardsVisible = false
    },
    togglePanel() {
      if (this.panelOpen) {
        this.closePanel()
        return
      }
      this.closeCards()
      this.panelOpen = true
      this.positionPanel()
      this.fetchNotices(false)
      this.$nextTick(() => {
        if (this.$refs.panel) {
          this.$refs.panel.focus()
        }
      })
    },
    closePanel() {
      this.panelOpen = false
    },
    positionPanel() {
      const bell = this.$refs.bell
      if (!bell) {
        return
      }
      const rect = bell.getBoundingClientRect()
      const width = 372
      const left = Math.min(
        Math.max(rect.right - width, 12),
        Math.max(window.innerWidth - width - 12, 12)
      )
      this.panelPos = {
        top: rect.bottom + 8,
        left
      }
    },
    handleResize() {
      this.viewportWidth = window.innerWidth
      if (this.panelOpen) {
        this.positionPanel()
      }
    },
    handleKeydown(event) {
      if (event.key === 'Escape' && this.panelOpen) {
        this.closePanel()
      }
    },
    handleWindowFocus() {
      if (this.stopped) {
        return
      }
      this.fetchNotices(false)
    },
    ackOne(item) {
      const index = this.notices.findIndex((notice) => notice.paperId === item.paperId)
      if (index < 0) {
        return
      }
      this.notices.splice(index, 1)
      if (!this.notices.length) {
        this.closeCards()
      }
      ackShareNotices([item.paperId]).catch((error) => {
        if (this.isSessionError(error)) {
          this.stopped = true
          return
        }
        this.notices.splice(Math.min(index, this.notices.length), 0, item)
        this.$message.error('操作失败，请稍后重试')
      })
    },
    ackAll() {
      if (!this.notices.length) {
        return
      }
      const snapshot = this.notices.slice()
      this.notices = []
      this.closeCards()
      ackAllShareNotices().catch((error) => {
        if (this.isSessionError(error)) {
          this.stopped = true
          return
        }
        this.notices = snapshot
        this.$message.error('操作失败，请稍后重试')
      })
    },
    openPaper(item) {
      this.ackOne(item)
      this.closePanel()
      this.closeCards()
      const paperId = String(item.paperId)
      const navigation = this.$router.push({ path: '/index', query: { paperId } })
      if (navigation && typeof navigation.catch === 'function') {
        navigation.catch(() => {})
      }
    },
    initialOf(item) {
      const name = (item.sharerName || '').trim()
      return name ? name.charAt(0) : '·'
    },
    avatarStyle(item) {
      const name = item.sharerName || item.paperId
      let hash = 0
      for (let i = 0; i < name.length; i += 1) {
        hash = (hash + name.charCodeAt(i)) % AVATAR_COLORS.length
      }
      return {
        background: AVATAR_COLORS[hash].bg,
        color: AVATAR_COLORS[hash].color
      }
    },
    formatTime(value) {
      const timestamp = Number(value)
      if (!timestamp) {
        return ''
      }
      const date = new Date(timestamp)
      const now = new Date()
      const diff = now.getTime() - timestamp
      if (diff >= 0 && diff < 60000) {
        return '刚刚'
      }
      if (diff >= 0 && diff < 3600000) {
        return `${Math.floor(diff / 60000)} 分钟前`
      }
      const clock = `${pad(date.getHours())}:${pad(date.getMinutes())}`
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
      if (date.getTime() >= startOfToday) {
        return `今天 ${clock}`
      }
      if (date.getTime() >= startOfToday - 86400000) {
        return `昨天 ${clock}`
      }
      if (date.getFullYear() === now.getFullYear()) {
        return `${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${clock}`
      }
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    }
  }
}
</script>

<style lang="scss" scoped>
.share-notice {
  display: inline-flex;
  align-items: center;
  height: 100%;
  line-height: 1.5;

  .share-notice__bell {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    margin: 0 2px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: #5a5e66;
    cursor: pointer;
    line-height: 1;
    transition: background 0.2s ease, color 0.2s ease;

    svg {
      width: 19px;
      height: 19px;
    }

    &:hover,
    &.is-open {
      background: rgba(24, 144, 255, 0.08);
      color: #1890ff;
    }

    &.has-unread {
      color: #1890ff;
    }
  }

  .share-notice__badge {
    position: absolute;
    top: 2px;
    right: 0;
    box-sizing: border-box;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    border-radius: 8px;
    border: 2px solid #fff;
    background: #ff4949;
    color: #fff;
    font-size: 10px;
    font-weight: 600;
    line-height: 12px;
    text-align: center;
  }

  .share-notice__mask {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 2100;
    background: transparent;

    &.is-sheet {
      background: rgba(15, 23, 42, 0.35);
    }
  }

  .share-notice__panel {
    position: fixed;
    z-index: 2200;
    width: 372px;
    display: flex;
    flex-direction: column;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 16px 44px rgba(15, 23, 42, 0.18), 0 2px 8px rgba(15, 23, 42, 0.08);
    outline: none;
    overflow: hidden;

    &.is-sheet {
      top: auto !important;
      left: 12px !important;
      right: 12px;
      bottom: 12px;
      width: auto;
      max-height: 72vh;
      border-radius: 14px;
    }
  }

  .sn-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px 10px;
    border-bottom: 1px solid #f2f3f5;

    h4 {
      margin: 0;
      font-size: 13.5px;
      font-weight: 600;
      color: #111827;
    }
  }

  .sn-list {
    max-height: 340px;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 3px;
      background: #dfe4ea;
    }
  }

  .sn-item {
    display: flex;
    gap: 10px;
    padding: 13px 16px;
    border-bottom: 1px solid #f7f8fa;
    background: #f7fbff;
  }

  .sn-avatar {
    flex: none;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 600;
  }

  .sn-item__main {
    flex: 1;
    min-width: 0;
  }

  .sn-item__head,
  .sn-card__head {
    display: flex;
    align-items: baseline;
    gap: 4px;
    font-size: 12.5px;
    color: #374151;

    b {
      font-weight: 600;
      color: #111827;
    }

    time {
      margin-left: auto;
      flex: none;
      font-size: 11.5px;
      color: #b0b7c3;
    }
  }

  .sn-item__paper,
  .sn-card__paper {
    margin-top: 3px;
    font-size: 12.5px;
    color: #111827;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .sn-item__ops,
  .sn-card__ops {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-top: 7px;
  }

  .sn-link {
    padding: 0;
    border: 0;
    background: none;
    color: #1890ff;
    font-size: 12px;
    line-height: 1.4;
    cursor: pointer;

    &:hover {
      color: #40a9ff;
    }

    &.is-muted {
      color: #909399;

      &:hover {
        color: #606266;
      }
    }
  }

  .sn-empty {
    padding: 34px 16px 30px;
    text-align: center;

    svg {
      width: 30px;
      height: 30px;
      color: #c0c8d4;
    }

    p {
      margin: 8px 0 0;
      font-size: 12.5px;
      color: #909399;
    }
  }

  .sn-foot {
    padding: 9px 16px;
    background: #fbfcfd;
    color: #a8b0bd;
    font-size: 11.5px;
  }

  .share-notice__cards {
    position: fixed;
    right: 18px;
    bottom: 18px;
    z-index: 2150;
    width: 384px;
    pointer-events: none;

    &.is-narrow {
      right: 12px;
      left: 12px;
      bottom: 12px;
      width: auto;
    }
  }

  .sn-card-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .sn-card {
    position: relative;
    display: flex;
    gap: 12px;
    padding: 14px 30px 12px 14px;
    background: #fff;
    border-radius: 10px;
    border-left: 3px solid #1890ff;
    box-shadow: 0 14px 36px rgba(15, 23, 42, 0.18), 0 2px 6px rgba(15, 23, 42, 0.06);
    pointer-events: auto;
  }

  .sn-card__main {
    flex: 1;
    min-width: 0;
  }

  .sn-card__close {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 22px;
    height: 22px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: #c0c4cc;
    font-size: 16px;
    line-height: 20px;
    cursor: pointer;

    &:hover {
      background: #f2f3f5;
      color: #909399;
    }
  }

  .sn-card__more {
    align-self: flex-start;
    padding: 6px 12px;
    border-radius: 14px;
    background: rgba(48, 65, 86, 0.82);
    color: #fff;
    font-size: 11.5px;
    line-height: 1.5;
    pointer-events: auto;
  }

  .sn-fade-enter-active,
  .sn-fade-leave-active {
    transition: opacity 0.18s ease;
  }

  .sn-fade-enter,
  .sn-fade-leave-to {
    opacity: 0;
  }

  .sn-panel-enter-active,
  .sn-panel-leave-active {
    transition: opacity 0.18s ease, transform 0.18s ease;
  }

  .sn-panel-enter,
  .sn-panel-leave-to {
    opacity: 0;
    transform: translateY(-6px);
  }

  .sn-card-enter-active {
    transition: opacity 0.24s ease, transform 0.24s ease;
  }

  .sn-card-leave-active {
    transition: opacity 0.18s ease, transform 0.18s ease;
    position: absolute;
    width: 100%;
  }

  .sn-card-enter,
  .sn-card-leave-to {
    opacity: 0;
    transform: translateY(12px);
  }
}
</style>
